import { apiFetch } from '../lib/api';
import { parseTonAmount } from '../lib/ton-format';
import { track } from '../lib/track';
import { useT } from '../i18n';
import { BRAND_NAME } from '../lib/app-variant';
import React, { useEffect, useState } from 'react';

interface VerifyProps {
    apiBase: string;
    initialFlipId?: string | null;
}

// UUIDv4 (8-4-4-4-12, version nibble = 4, variant nibble in {8,9,a,b}).
// Generated client-side via crypto.randomUUID() at flip-request time and
// echoed back by the backend in the flip-request response and history feed.
const UUID_V4_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

type VerifyStatus = 'SETTLED' | 'PENDING' | 'FAILED' | 'FAILED_REFUNDED';

interface SettledData {
    status: 'SETTLED';
    flipRequestId: string;
    commitHashHex: string;
    serverSeedHex: string;
    clientSeedHex: string;
    nonce: number;
    stakeNano: string;
    choice: 0 | 1;
    outcome: 0 | 1;
    won: boolean;
    hmacHex: string;
    settledAt: number;
    chainId: number;
    seedIndex: number;
    terminatorHashHex: string;
    // In-browser recomputation results (Web Crypto SHA-256 + HMAC-SHA256).
    computedCommitHashHex: string;
    commitValid: boolean;
    computedHmacHex: string;
    hmacValid: boolean;
    computedOutcome: 0 | 1;
    outcomeValid: boolean;
}

interface NonSettledData {
    status: 'PENDING' | 'FAILED' | 'FAILED_REFUNDED';
    flipRequestId: string;
    commitHashHex: string;
    message: string;
}

type VerifyData = SettledData | NonSettledData;

function hexToBytes(hex: string): Uint8Array {
    const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
    if (clean.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(clean)) {
        throw new Error('invalid_hex_string');
    }
    // Allocate via explicit ArrayBuffer so the Uint8Array buffer type is
    // ArrayBuffer (not ArrayBufferLike with SharedArrayBuffer in the union).
    // crypto.subtle.* requires BufferSource = ArrayBuffer-backed view.
    const buf = new ArrayBuffer(clean.length / 2);
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Recompute the chain client-side using Web Crypto. Two independent checks:
 *
 *   1. commit_hash := SHA-256(server_seed)        — proves server_seed was
 *      pre-committed BEFORE the flip (commit_hash was returned by
 *      /next-commit before any client_seed existed).
 *
 *   2. hmac := HMAC-SHA256(key = server_seed, msg = canonical 73-byte msg)
 *      where canonical msg = server_seed (32B) || client_seed (32B) ||
 *      nonce (8B big-endian uint64) || choice (1B; 0=heads, 1=tails).
 *      outcome := hmac[0] & 1.
 *
 * If both recomputed values match what the backend returned, the result is
 * verifiable: a third party with the same four inputs cannot land on a
 * different outcome. This runs in the user's browser — we are not the
 * computer that says it's fair.
 */
async function recomputeChain(
    serverSeedHex: string,
    clientSeedHex: string,
    nonce: number,
    choice: 0 | 1,
): Promise<{ commitHashHex: string; hmacHex: string; outcome: 0 | 1 }> {
    const serverSeed = hexToBytes(serverSeedHex);
    if (serverSeed.length !== 32) throw new Error('server_seed_must_be_32_bytes');
    const clientSeed = hexToBytes(clientSeedHex);
    if (clientSeed.length !== 32) throw new Error('client_seed_must_be_32_bytes');

    const commitHashBuf = await crypto.subtle.digest('SHA-256', serverSeed.buffer as ArrayBuffer);
    const commitHashHex = bytesToHex(new Uint8Array(commitHashBuf));

    const msgBuf = new ArrayBuffer(73);
    const msg = new Uint8Array(msgBuf);
    msg.set(serverSeed, 0);
    msg.set(clientSeed, 32);
    const view = new DataView(msgBuf);
    view.setBigUint64(64, BigInt(nonce), false); // big-endian
    msg[72] = choice;

    const key = await crypto.subtle.importKey(
        'raw',
        serverSeed.buffer as ArrayBuffer,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign'],
    );
    const hmacBuf = await crypto.subtle.sign('HMAC', key, msgBuf);
    const hmacBytes = new Uint8Array(hmacBuf);
    const hmacHex = bytesToHex(hmacBytes);
    const outcome = (hmacBytes[0] & 1) as 0 | 1;

    return { commitHashHex, hmacHex, outcome };
}

// CF-WITHDRAW-DISPLAY-PRECISION-V1: the local 2-decimal `nanoToTon` truncation was
// removed in favour of the canonical EXACT formatter (lib/ton-format.ts).
// `parseTonAmount` preserves the fail-loud {ok,reason} contract (R6 Binding 6: a
// malformed economic value renders an explicit invalid state on the trust path,
// never a coerced zero) and shows the exact value.

function choiceLabel(c: 0 | 1): string {
    return c === 0 ? 'HEADS' : 'TAILS';
}

function buildTerminalCommand(s: SettledData): string {
    return `node -e "const c=require('crypto');const ss=Buffer.from('${s.serverSeedHex}','hex');const cs=Buffer.from('${s.clientSeedHex}','hex');const m=Buffer.alloc(73);ss.copy(m,0);cs.copy(m,32);m.writeBigUInt64BE(${s.nonce}n,64);m.writeUInt8(${s.choice},72);console.log(c.createHmac('sha256',ss).update(m).digest('hex'))"`;
}

export function Verify({ apiBase, initialFlipId }: VerifyProps) {
    const t = useT();
    // Display-only translated HEADS/TAILS. The module-level choiceLabel() stays
    // English because it also feeds the fixed-width copy-proof export block (which
    // is intentionally left English). Use this for on-screen labels only.
    const choiceLabelT = (c: 0 | 1): string => t(c === 0 ? 'common.heads' : 'common.tails');
    const [flipId, setFlipId] = useState(initialFlipId ?? '');
    const [data, setData] = useState<VerifyData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (initialFlipId) setFlipId(initialFlipId);
    }, [initialFlipId]);

    // CF-MAINNET-91: trust-signal engagement metric. Fires once on mount per tab open.
    useEffect(() => {
        track.verifyTabOpened();
    }, []);

    const verify = async () => {
        const trimmed = flipId.trim();
        if (!trimmed) return;
        if (!UUID_V4_REGEX.test(trimmed)) {
            setError(t('verify.error.invalid_uuid'));
            return;
        }

        setLoading(true);
        setData(null);
        setError(null);

        try {
            const resp = await apiFetch(`${apiBase}/api/v1/verify/${trimmed}`);
            if (resp.status === 404) {
                setError(t('verify.error.not_found', { id: trimmed }));
                setLoading(false);
                return;
            }
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
            }
            const body = await resp.json();
            const status: VerifyStatus = body.status;

            if (status === 'PENDING' || status === 'FAILED' || status === 'FAILED_REFUNDED') {
                setData({
                    status,
                    flipRequestId: trimmed,
                    commitHashHex: body.commit_hash_hex,
                    message: body.message ?? '',
                });
                setLoading(false);
                return;
            }

            if (status !== 'SETTLED') {
                throw new Error(`unexpected_status: ${status}`);
            }

            const computed = await recomputeChain(
                body.server_seed_hex,
                body.client_seed_hex,
                body.nonce,
                body.choice as 0 | 1,
            );

            setData({
                status: 'SETTLED',
                flipRequestId: trimmed,
                commitHashHex: body.commit_hash_hex,
                serverSeedHex: body.server_seed_hex,
                clientSeedHex: body.client_seed_hex,
                nonce: body.nonce,
                stakeNano: body.stake_nano,
                choice: body.choice as 0 | 1,
                outcome: body.outcome as 0 | 1,
                won: !!body.won,
                hmacHex: body.hmac_hex,
                settledAt: body.settled_at,
                chainId: body.chain_id,
                seedIndex: body.seed_index,
                terminatorHashHex: body.terminator_hash_hex,
                computedCommitHashHex: computed.commitHashHex,
                commitValid: computed.commitHashHex.toLowerCase() === String(body.commit_hash_hex).toLowerCase(),
                computedHmacHex: computed.hmacHex,
                hmacValid: computed.hmacHex.toLowerCase() === String(body.hmac_hex).toLowerCase(),
                computedOutcome: computed.outcome,
                outcomeValid: computed.outcome === (body.outcome as 0 | 1),
            });
        } catch (err) {
            console.error('🚨 verify_fetch_or_recompute_failed', err);
            setError(t('verify.error.fetch_failed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pb-4">
            {/* Header */}
            <div className="text-center mb-5">
                <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         style={{ color: 'var(--emerald)' }}>
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <path d="M9 12l2 2 4-4" />
                    </svg>
                </div>
                <h2 className="text-lg font-black tracking-tight">{t('verify.title')}</h2>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                    {t('verify.subtitle')}
                </p>
            </div>

            {/* Search */}
            <div className="flex gap-2 mb-5">
                <div className="flex-1 relative">
                    <input
                        type="text"
                        inputMode="text"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={flipId}
                        onChange={e => { setFlipId(e.target.value); setError(null); }}
                        placeholder={t('verify.input.placeholder')}
                        className="w-full rounded-xl px-4 py-3 text-sm font-medium font-mono outline-none transition-all"
                        style={{
                            background: 'var(--bg-card)',
                            border: `1px solid ${error ? 'rgba(239, 68, 68, 0.3)' : 'var(--border-subtle)'}`,
                            color: 'var(--text-primary)',
                        }}
                        onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                        onBlur={e => e.target.style.borderColor = error ? 'rgba(239, 68, 68, 0.3)' : 'var(--border-subtle)'}
                    />
                </div>
                <button
                    id="verify-btn"
                    onClick={verify}
                    disabled={!flipId || loading}
                    className="btn-primary px-5 rounded-xl text-sm font-bold"
                >
                    {loading ? (
                        <span className="inline-block w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    ) : t('verify.button.verify')}
                </button>
            </div>

            {/* Error message */}
            {error && (
                <div className="rounded-xl p-3.5 mb-5 flex items-start gap-2.5"
                     style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)' }}>
                    <span className="text-base flex-shrink-0">⚠️</span>
                    <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                        {error}
                    </p>
                </div>
            )}

            {/* Non-settled states */}
            {data && data.status !== 'SETTLED' && (
                <div className="rounded-2xl p-4 mb-4"
                     style={{ background: 'rgba(244, 213, 111, 0.05)', border: '1px solid rgba(244, 213, 111, 0.15)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-base">⏳</span>
                        <span className="text-[12px] font-bold">{data.status}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                        {data.status === 'PENDING' && t('verify.status.pending_body')}
                        {data.status === 'FAILED' && t('verify.status.failed_body')}
                        {data.status === 'FAILED_REFUNDED' && t('verify.status.failed_refunded_body')}
                    </p>
                    {data.commitHashHex && (
                        <div className="mt-3 font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            <p style={{ color: 'var(--text-muted)' }}>{t('verify.commit_hash_prebet_label')}</p>
                            <p className="truncate" style={{ color: 'var(--text-secondary)' }}>{data.commitHashHex}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Settled — full verification */}
            {data && data.status === 'SETTLED' && (
                <div className="space-y-3">
                    {/* Result Banner */}
                    <div className="rounded-2xl p-4"
                         style={{
                             background: data.won ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                             border: `1px solid ${data.won ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.1)'}`,
                         }}>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">{data.won ? '✅' : '❌'}</span>
                                    <span className="text-[12px] font-bold font-mono">
                                        {data.flipRequestId.slice(0, 8)}…
                                    </span>
                                </div>
                                <p className="text-[11px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    {t('verify.result.picked_result', { choice: choiceLabelT(data.choice), outcome: choiceLabelT(data.outcome) })}
                                </p>
                            </div>
                            <div className="text-right">
                                {(() => {
                                    const stakeResult = parseTonAmount(data.stakeNano);
                                    if (!stakeResult.ok) {
                                        return (
                                            <p className="text-[10px] font-bold" style={{ color: 'var(--red)' }}>
                                                {t('verify.error.invalid_stake')}
                                            </p>
                                        );
                                    }
                                    return (
                                        <>
                                            <p className="text-lg font-black"
                                               style={{ color: data.won ? 'var(--emerald)' : 'var(--red)' }}>
                                                {data.won ? '+' : '-'}{stakeResult.ton}
                                            </p>
                                            <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>TON</p>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Step 1: Commit Integrity */}
                    <div className="glass rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
                                  style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--emerald)' }}>1</span>
                            <span className="text-[12px] font-bold" style={{ color: 'var(--emerald)' }}>{t('verify.step1.title')}</span>
                            <span className="text-[10px] ml-auto font-bold"
                                  style={{ color: data.commitValid ? 'var(--emerald)' : 'var(--red)' }}>
                                {data.commitValid ? t('verify.status.valid_badge') : t('verify.status.mismatch_badge')}
                            </span>
                        </div>
                        <div className="space-y-1.5 font-mono text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                            <p style={{ color: 'var(--text-muted)' }}>{t('verify.step1.sha256_label')}</p>
                            <p className="break-all">{data.computedCommitHashHex}</p>
                            <p style={{ color: 'var(--text-muted)' }}>{t('verify.step1.precommitted_label')}</p>
                            <p className="break-all">{data.commitHashHex}</p>
                            <p style={{ color: data.commitValid ? 'var(--emerald)' : 'var(--red)' }}>
                                {data.commitValid
                                    ? t('verify.step1.valid_msg')
                                    : t('verify.step1.mismatch_msg')}
                            </p>
                        </div>
                    </div>

                    {/* Step 2: HMAC Recomputation */}
                    <div className="glass rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
                                  style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>2</span>
                            <span className="text-[12px] font-bold" style={{ color: '#3b82f6' }}>{t('verify.step2.title')}</span>
                            <span className="text-[10px] ml-auto font-bold"
                                  style={{ color: data.hmacValid ? 'var(--emerald)' : 'var(--red)' }}>
                                {data.hmacValid ? t('verify.status.valid_badge') : t('verify.status.mismatch_badge')}
                            </span>
                        </div>
                        <div className="space-y-2 font-mono text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                            <div>
                                <p style={{ color: 'var(--text-muted)' }}>{t('verify.step2.server_seed_label')}</p>
                                <p className="break-all">{data.serverSeedHex}</p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-muted)' }}>{t('verify.step2.client_seed_label')}</p>
                                <p className="break-all">{data.clientSeedHex}</p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-muted)' }}>{t('verify.step2.nonce_label')}</p>
                                <p>{data.nonce}</p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-muted)' }}>{t('verify.step2.choice_label')}</p>
                                <p>{choiceLabelT(data.choice)} ({data.choice})</p>
                            </div>
                            <div className="h-px" style={{ background: 'var(--border-subtle)' }} />
                            <div>
                                <p style={{ color: 'var(--text-muted)' }}>{t('verify.step2.recomputed_label')}</p>
                                <p className="break-all">{data.computedHmacHex}</p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-muted)' }}>{t('verify.step2.backend_hmac_label')}</p>
                                <p className="break-all">{data.hmacHex}</p>
                            </div>
                            <p style={{ color: data.hmacValid ? 'var(--emerald)' : 'var(--red)' }}>
                                {data.hmacValid
                                    ? t('verify.step2.valid_msg')
                                    : t('verify.step2.mismatch_msg')}
                            </p>
                        </div>
                    </div>

                    {/* Step 3: Outcome */}
                    <div className="glass rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
                                  style={{ background: 'rgba(244, 213, 111, 0.15)', color: 'var(--gold)' }}>3</span>
                            <span className="text-[12px] font-bold" style={{ color: 'var(--gold)' }}>{t('verify.step3.title')}</span>
                            <span className="text-[10px] ml-auto font-bold"
                                  style={{ color: data.outcomeValid ? 'var(--emerald)' : 'var(--red)' }}>
                                {data.outcomeValid ? t('verify.status.matches_badge') : t('verify.status.mismatch_badge')}
                            </span>
                        </div>
                        <div className="space-y-1.5 font-mono text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                            <p>hmac[0] = <span className="font-bold" style={{ color: 'var(--text-primary)' }}>0x{data.hmacHex.slice(0, 2)}</span> · low bit = <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{data.computedOutcome}</span></p>
                            <p>{data.computedOutcome === 0 ? `0 → ${choiceLabelT(0)}` : `1 → ${choiceLabelT(1)}`}</p>
                            <p className="font-bold" style={{ color: 'var(--gold)' }}>
                                → {choiceLabelT(data.outcome)} {data.won ? '✅' : ''}
                            </p>
                        </div>
                    </div>

                    {/* Step 4: Verify Yourself */}
                    <div className="glass rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
                                  style={{ background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' }}>4</span>
                            <span className="text-[12px] font-bold" style={{ color: '#a855f7' }}>{t('verify.step4.title')}</span>
                        </div>
                        <p className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>
                            {t('verify.step4.run_instruction')}
                        </p>
                        <div className="rounded-lg p-2.5 font-mono text-[9px] leading-relaxed overflow-x-auto whitespace-pre-wrap break-all"
                             style={{ background: 'var(--bg-deep)', color: 'var(--text-secondary)' }}>
                            {buildTerminalCommand(data)}
                        </div>

                        <div className="flex gap-2 mt-3">
                            <button
                                className="flex-1 glass rounded-lg py-2.5 text-[10px] font-bold text-center active:scale-95 transition-all"
                                style={{ color: 'var(--text-secondary)' }}
                                onClick={() => {
                                    const text = [
                                        `${BRAND_NAME} — Verification Proof`,
                                        '═══════════════════════════════',
                                        `Flip Request ID:  ${data.flipRequestId}`,
                                        `Status:           ${data.status}`,
                                        `Chain:            #${data.chainId} · seed_index ${data.seedIndex}`,
                                        '',
                                        `Server Seed:      ${data.serverSeedHex}`,
                                        `Client Seed:      ${data.clientSeedHex}`,
                                        `Nonce:            ${data.nonce}`,
                                        `Choice:           ${data.choice} (${choiceLabel(data.choice)})`,
                                        '',
                                        `Commit Hash:      ${data.commitHashHex}`,
                                        `SHA256(seed):     ${data.computedCommitHashHex}  ${data.commitValid ? '✓ MATCH' : '✗ MISMATCH'}`,
                                        '',
                                        `HMAC (backend):   ${data.hmacHex}`,
                                        `HMAC (computed):  ${data.computedHmacHex}  ${data.hmacValid ? '✓ MATCH' : '✗ MISMATCH'}`,
                                        `Outcome:          ${data.outcome} (${choiceLabel(data.outcome)})  ${data.outcomeValid ? '✓ MATCH' : '✗ MISMATCH'}`,
                                        `Won:              ${data.won ? 'YES' : 'NO'}`,
                                        '',
                                        `Terminal:         ${buildTerminalCommand(data)}`,
                                    ].join('\n');
                                    navigator.clipboard?.writeText(text).then(() => {
                                        const el = document.getElementById('copy-btn');
                                        if (el) { el.textContent = t('verify.copy.copied'); setTimeout(() => { el.textContent = t('verify.copy.default'); }, 1500); }
                                    }).catch(() => {});
                                }}
                            >
                                <span id="copy-btn">{t('verify.copy.default')}</span>
                            </button>
                        </div>
                    </div>

                    {/* What am I looking at? */}
                    <details className="glass rounded-2xl p-4">
                        <summary className="text-[11px] font-bold cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                            {t('verify.explainer.summary')}
                        </summary>
                        <div className="mt-3 space-y-2 text-[10px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                            {/* CF-JINLONGHU-VERIFY-LEAK-V1 (B-7): keyed deep prose; label reuses verify.step{n}.title. */}
                            {[
                                { n: 1, color: 'var(--emerald)', bodyKey: 'verify.explainer.step1_body' },
                                { n: 2, color: '#3b82f6', bodyKey: 'verify.explainer.step2_body' },
                                { n: 3, color: 'var(--gold)', bodyKey: 'verify.explainer.step3_body' },
                                { n: 4, color: '#a855f7', bodyKey: 'verify.explainer.step4_body' },
                            ].map(item => (
                                <p key={item.n}>
                                    <strong style={{ color: item.color }}>
                                        {t('verify.explainer.step_label', { n: item.n, title: t(`verify.step${item.n}.title`) })}
                                    </strong>{' '}
                                    {t(item.bodyKey)}
                                </p>
                            ))}
                        </div>
                    </details>
                </div>
            )}

            {/* How it works — show when no verification active */}
            {!data && !error && (
                <div className="mt-2">
                    <p className="text-[10px] font-semibold tracking-[0.2em] uppercase mb-4"
                       style={{ color: 'var(--text-muted)' }}>
                        {t('verify.howitworks.header')}
                    </p>

                    <div className="space-y-3">
                        {[
                            {
                                step: '1',
                                titleKey: 'verify.howitworks.step1_title',
                                descKey: 'verify.howitworks.step1_desc',
                                color: 'var(--emerald)',
                                bg: 'rgba(16, 185, 129, 0.1)',
                            },
                            {
                                step: '2',
                                titleKey: 'verify.howitworks.step2_title',
                                descKey: 'verify.howitworks.step2_desc',
                                color: '#3b82f6',
                                bg: 'rgba(59, 130, 246, 0.1)',
                            },
                            {
                                step: '3',
                                titleKey: 'verify.howitworks.step3_title',
                                descKey: 'verify.howitworks.step3_desc',
                                color: 'var(--gold)',
                                bg: 'rgba(244, 213, 111, 0.1)',
                            },
                            {
                                step: '4',
                                titleKey: 'verify.howitworks.step4_title',
                                descKey: 'verify.howitworks.step4_desc',
                                color: '#a855f7',
                                bg: 'rgba(168, 85, 247, 0.1)',
                            },
                        ].map(item => (
                            <div key={item.step} className="glass rounded-xl p-3.5 flex gap-3">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-black"
                                     style={{ background: item.bg, color: item.color }}>
                                    {item.step}
                                </div>
                                <div>
                                    <p className="text-[12px] font-bold" style={{ color: item.color }}>{t(item.titleKey)}</p>
                                    <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                        {t(item.descKey)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="glass-gold rounded-xl p-3 mt-5 text-center">
                        <p className="text-[12px] font-bold" style={{ color: 'var(--gold)' }}>
                            {t('verify.tagline')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
